import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { resource } from '@util/resource';
import { PreorderLink, PreorderLinkOptions } from './link.service';

export interface WineryInfo {
  name: string;
  avatar: string;
  privacyStatementUrl: string;
  activeBillingDetails?: boolean;
}

/** Response from GET wspayments/accounts/winery/{wineryId}/dwspay */
export interface DwspayStatusResponse {
  enabled?: boolean;
  connected?: boolean;
  stripe_account_connect_id?: string;
}

/** Response from GET wspayments/accounts/winery/{wineryId}/payment-methods */
export interface PaymentMethodsResponse {
  payment_methods?: string[];
  paymentMethods?: string[];
}

@Injectable({
  providedIn: 'root'
})
// Os atributos wineryId e channelId estão aqui para manter o wineryId, channelId na troca de paginas
// Por favor não seja esperto
export class WineryInfoService {
  public wineryId: string;
  public channelId: string;
  public linkId: string;
  private wineryInfo$: ReplaySubject<WineryInfo> = new ReplaySubject<WineryInfo>(1);
  private wineryOptions$: ReplaySubject<PreorderLinkOptions> = new ReplaySubject<PreorderLinkOptions>(1);
  private stripeAccountId$: ReplaySubject<string> = new ReplaySubject<string>(1);
  /** Current dwspay status for the winery. */
  private dwspayEnabled$: BehaviorSubject<DwspayStatusResponse> =
    new BehaviorSubject<DwspayStatusResponse>({ enabled: false });

  constructor() {
  const cachedWineryInfo = sessionStorage.getItem('wineryInfo');
  if (cachedWineryInfo) {
    this.wineryInfo$.next(JSON.parse(cachedWineryInfo));
  }

  const cachedIds = sessionStorage.getItem('wineryMeta');
  if (cachedIds) {
    const { wineryId, channelId, linkId } = JSON.parse(cachedIds);

    this.wineryId = wineryId;
    this.channelId = channelId;
    this.linkId = linkId;
  }
}


  getWineryDetail(): Observable<WineryInfo> {
    return this.wineryInfo$;
  }

  getWineryOptions(): Observable<PreorderLinkOptions> {
    return this.wineryOptions$;
  }

  getStripeAccountId(): Observable<string> {
    return this.stripeAccountId$;
  }

  /** Dwspay status for the current winery (from wspayments API). Emits after loadOptions has run and the API was called. */
  getDwspayEnabled(): Observable<DwspayStatusResponse> {
    return this.dwspayEnabled$;
  }

  /** Payment methods for the winery (from wspayments API). Used to gate dwspay visibility (card, satispay, klarna, etc.). */
  getPaymentMethods(wineryId: string): Observable<PaymentMethodsResponse> {
    return resource('wspayments://accounts/winery/payment-methods')
      .params({ wineryId })
      .get<PaymentMethodsResponse>();
  }

public load(wineryId: string, channelId: string) {
  this.wineryId = wineryId;
  this.channelId = channelId;

  // Salva IDs in sessionStorage
  sessionStorage.setItem('wineryMeta', JSON.stringify({
    wineryId,
    channelId,
    linkId: this.linkId // già impostato da loadOptions()
  }));
  resource('preorders://winery/wineryId')
    .params({ wineryId })
    .get<WineryInfo>()
    .subscribe(wineryInfo => {
      // Salva anche il dettaglio nel sessionStorage
      sessionStorage.setItem('wineryInfo', JSON.stringify(wineryInfo));
      this.wineryInfo$.next(wineryInfo);
    });
}


public loadOptions(linkId: string) {
  this.linkId = linkId;

  // Aggiorna anche il sessionStorage se già presente
  const meta = sessionStorage.getItem('wineryMeta');
  const parsed = meta ? JSON.parse(meta) : {};
  parsed.linkId = linkId;
  sessionStorage.setItem('wineryMeta', JSON.stringify(parsed));

  resource('preorders://link/id')
    .params({ id: linkId })
    .get<PreorderLink>()
    .subscribe(pl => {
      this.wineryOptions$.next(pl.options);
      this.stripeAccountId$.next(pl.stripeAccountId);
      this.fetchDwspayEnabled(pl.wineryId);
    });
}

  /** Call wspayments API to get dwspay status for the winery. Also checks payment methods (card, satispay, klarna, etc.). */
  private fetchDwspayEnabled(wineryId: string): void {
    resource('wspayments://accounts/winery/dwspay')
      .params({ wineryId })
      .get<DwspayStatusResponse>()
      .pipe(
        catchError(err => {
          console.error('fetchDwspayEnabled failed', err);
          return of({ enabled: false } as DwspayStatusResponse);
        })
      )
      .subscribe(dwspayStatus => {
        if (!dwspayStatus.enabled) {
          this.dwspayEnabled$.next(dwspayStatus);
          return;
        }
        this.getPaymentMethods(wineryId)
          .pipe(
            catchError(err => {
              console.error('getPaymentMethods failed', err);
              return of({ payment_methods: [], paymentMethods: [] });
            })
          )
          .subscribe(pmResponse => {
            const methods = pmResponse.payment_methods || pmResponse.paymentMethods || [];
            const allowedTypes = ['card', 'satispay', 'klarna', 'google_pay', 'apple_pay'];
            const hasAllowedPaymentMethods =
              methods.length === 0 || methods.some((m: string) => allowedTypes.includes(m));
            this.dwspayEnabled$.next({
              ...dwspayStatus,
              enabled: dwspayStatus.enabled && hasAllowedPaymentMethods,
            });
          });
      });
  }
}
